import { AxiosRequestConfig, AxiosResponse } from "axios";
import { parseApiData } from "go-core/api/parseApiData";
import { getSearchSelectPage, mapToBase } from "go-core/api/searchSelectService";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { CustomFieldTemplateApi, CustomFieldTemplateFormApi, SegmentApi } from "go-segment/services/types";
import { restApi } from "../../Security/api";
import {
	SearchSupportDistrictApi,
	SupportAccessOrganizationApi,
	SupportDistrictApi,
	SupportOrganizationApi,
} from "../../support/types";
import { AddPartnerFormProps, PartnerApi, PartnerSearchSelectApi, PartnerSummaryApi } from "../Partner/partnerTypes";
import { SubscriptionInvoiceApi, SubscriptionsApi } from "../Subscription/subscriptionTypes";
import {
	AppVersionSearchSelectApi,
	LoanApi,
	OrganizationApi,
	OrganizationApplicationApi,
	OrganizationFeatureApi,
	OrganizationFormProps,
	OrganizationProductApi,
	OrganizationProductFormApi,
	OrganizationProductPerCategoryApi,
	OrganizationTypeApi,
	OrganizationsDashboardApi,
	PayoutInvoiceApi,
	PayoutInvoiceFormProps,
	RemoveOrganizationApi,
	SearchSelectApi,
	UserApi,
	UserRole,
} from "../types";
import {
	AccessApi,
	AdminOrganizationApi,
	AdminOrganizationUserApi,
	AnomaliesPartnerSettlementApi,
	AppVersionApi,
	ApplicationTemplateApi,
	BillingCustomerApi,
	CancelLoanFormProps,
	CrashReportApi,
	CrashReportFormApi,
	CrashReportOccurrenceApi,
	FileLogApi,
	GoLauncherApplicationApi,
	GoLauncherCategoryApi,
	IntercomConversationApi,
	MergeOrganizationApi,
	OrganizationAccessApi,
	OrganizationPartnerInfoApi,
	PayoffTransactionFormProps,
	ProductCatalogApi,
	SyncCompaniesFormProps,
	SyncOrganizationFeatureReportApi,
	SyncTransactionReportApi,
	TerminalApi,
	TransactionApi,
	UpdateBillingDateApi,
	WrongInvoiceApi,
} from "./adminTypes";

class adminApi {
	static async getAdminTerminals(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<TerminalApi[]> {
		return parseApiData(restApi.get(`/admin/terminals`, { params, cancelToken: options?.cancelToken }));
	}

	static async getAdminTerminalsReport(params?: Record<string, any>, options?: Record<string, any>) {
		return restApi.get(`/admin/terminals/report`, { params, cancelToken: options?.cancelToken });
	}

	static async getAdminDevicesReport(params?: Record<string, any>, options?: Record<string, any>) {
		return restApi.get(`/admin/devices/report`, { params, cancelToken: options?.cancelToken });
	}

	static async getAdminPartnerReport(params?: Record<string, any>, options?: Record<string, any>) {
		return restApi.get(`/admin/partners/report`, { params, cancelToken: options?.cancelToken });
	}

	static async getAdminOrganizationReport(params?: Record<string, any>, options?: Record<string, any>) {
		return restApi.get(`/admin/organizations/report`, { params, cancelToken: options?.cancelToken });
	}

	static async getPartnerPayoff(id: string): Promise<PartnerSummaryApi> {
		return parseApiData(restApi.get(`/admin/partners/summary/${id}`));
	}

	static async getPartnersPayoffs(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<PartnerSummaryApi[]> {
		return parseApiData(restApi.get(`/admin/partners/summary`, { params, cancelToken: options?.cancelToken }));
	}

	static async summarizePartnersPayoffs(params?: Record<string, any>) {
		return restApi.put(`/admin/partners/summarize`, undefined, { params });
	}

	static async addPartnerToOrganization(id: number, data: AddPartnerFormProps) {
		return restApi.put(`admin/organizations/${id}/partners`, data);
	}

	static async getAdminPartnersSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<PartnerSearchSelectApi[]> {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);
		const res = await parseApiData(
			restApi.get(`admin/partners/search_select?f=${f}&page=${page}`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);

		return res.map((partner: PartnerSearchSelectApi) => {
			return {
				id: partner.id,
				name: partner.label,
				email: partner.email,
			};
		});
	}

	static async getListAdminPartnersSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>,
		partnerId?: string | number
	): Promise<AxiosResponse> {
		const newParams = {
			...params,
			"id|ne": partnerId ? partnerId : -1,
		};
		const f = mapToBase(search, newParams);
		const page = getSearchSelectPage(newParams);
		return restApi.get(`admin/partners/search_select?f=${f}&page=${page}`, {
			cancelToken: options?.cancelToken,
		});
	}

	static async getListAdminOrganizationsListSearchSelect(
		search: string,
		params: Record<string, any>,
		externalOrg?: string,
		options?: Record<string, any>
	): Promise<SearchSelectApi[]> {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);
		return parseApiData(
			restApi.get(`admin/organizations/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getListAdminOrganizationsSearchSelect(
		search?: string,
		params?: Record<string, any>
	): Promise<SearchSelectApi[]> {
		const f = mapToBase(search || "", params || {});
		return parseApiData(restApi.get(`admin/organizations/search_select?f=${f}`));
	}

	static async getAdminPartnersListSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);
		return restApi.get(`admin/partners/search_select?f=${f}&page=${page}`, {
			cancelToken: options?.cancelToken,
		});
	}

	static async enableAdminPartner(partnerId: number, params?: Record<string, any>): Promise<PartnerApi> {
		return parseApiData(restApi.put(`/admin/partners/${partnerId}/enable`, { params }));
	}

	static async disableAdminPartner(partnerId: number, params?: Record<string, any>): Promise<PartnerApi> {
		return parseApiData(restApi.put(`/admin/partners/${partnerId}/disable`, { params }));
	}

	static async restoreAdminPartner(partnerId: number, params?: Record<string, any>): Promise<PartnerApi> {
		return parseApiData(restApi.put(`/admin/partners/${partnerId}/restore`, { params }));
	}

	static async removeAdminPartner(partnerId?: number, params?: Record<string, any>): Promise<AxiosResponse> {
		return restApi.delete(`/admin/partners/${partnerId}`, { params });
	}

	static async updateAdminPartner(
		partnerId: number,
		data: PartnerApi,
		params?: Record<string, any>
	): Promise<PartnerApi> {
		return parseApiData(restApi.put(`/admin/partners/${partnerId}`, data, { params }));
	}

	static async createAdminPartner(data: PartnerApi): Promise<PartnerApi> {
		return parseApiData(restApi.post(`/admin/partners`, data));
	}

	static async getAdminPartner(id: number, params?: Record<string, any>): Promise<PartnerApi> {
		return parseApiData(restApi.get(`/admin/partners/${id}`, { params }));
	}

	static async getAdminPartners(params?: Record<string, any>, options?: Record<string, any>): Promise<PartnerApi[]> {
		return parseApiData(restApi.get(`/admin/partners`, { params, cancelToken: options?.cancelToken }));
	}

	static async updateAppVersion(id: number, data: AppVersionApi) {
		return restApi.put(`/admin/app_versions/${id}`, data);
	}

	static async createAppVersion(data: FormData): Promise<AxiosResponse> {
		return restApi.post(`/admin/app_versions`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
	}

	static async removeAppVersion(id: number): Promise<AxiosResponse> {
		return restApi.delete(`/admin/app_versions/${id}`);
	}

	static async getAppVersion(id: number): Promise<AppVersionApi> {
		return parseApiData(restApi.get(`/admin/app_versions/${id}`));
	}

	static async getAppVersions(params?: Record<string, any>, options?: Record<string, any>): Promise<AppVersionApi[]> {
		return parseApiData(restApi.get(`admin/app_versions`, { params, cancelToken: options?.cancelToken }));
	}

	static async updateApp(
		data: ApplicationTemplateApi,
		params?: Record<string, any>
	): Promise<ApplicationTemplateApi> {
		return parseApiData(restApi.put(`/admin/app_templates/${data.id}`, data, { params }));
	}

	static async createApp(data: ApplicationTemplateApi): Promise<ApplicationTemplateApi> {
		return parseApiData(restApi.post(`/admin/app_templates`, data));
	}

	static async removeApp(id: number): Promise<AxiosResponse> {
		return restApi.delete(`/admin/app_templates/${id}`);
	}

	static async getApp(id: number): Promise<ApplicationTemplateApi> {
		return parseApiData(restApi.get(`/admin/app_templates/${id}`));
	}

	static async getApps(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<ApplicationTemplateApi[]> {
		return parseApiData(restApi.get(`admin/app_templates`, { params, cancelToken: options?.cancelToken }));
	}

	static async updateAdminUserRoles(userId: number, data: UserRole[]): Promise<UserApi> {
		return parseApiData(restApi.put(`/admin/users/${userId}/roles`, { roles: data }));
	}

	static async updateAdminUser(userId: number, data: UserApi, params?: Record<string, any>): Promise<UserApi> {
		return parseApiData(restApi.put(`/admin/users/${userId}`, data, { params }));
	}

	static async createAdminUser(data: UserApi, params?: Record<string, any>): Promise<UserApi> {
		return parseApiData(restApi.post(`/admin/users`, data, { params }));
	}

	static async removeUser(userId: number, data: { reason: string }): Promise<AxiosResponse> {
		return restApi.delete(`/admin/users/${userId}`, { data });
	}

	static async getUser(id: number): Promise<UserApi> {
		return parseApiData(restApi.get(`/admin/users/${id}`));
	}

	static async getFileLogs(params?: Record<string, any>, options?: Record<string, any>): Promise<FileLogApi[]> {
		return parseApiData(restApi.get("/admin/file_logs", { params, cancelToken: options?.cancelToken }));
	}

	static async updateCrashReport(
		crashReport: CrashReportApi,
		data: CrashReportFormApi,
		params?: Record<string, any>
	): Promise<CrashReportApi> {
		return parseApiData(restApi.put(`/admin/crash_reports/${crashReport.id}`, data, { params }));
	}

	static async getCrashReportsOccurrences(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<CrashReportOccurrenceApi[]> {
		return parseApiData(
			restApi.get(`/admin/crash_reports/occurrences`, { params, cancelToken: options?.cancelToken })
		);
	}

	static async getCrashReportsTerminalsSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	) {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);

		return restApi.get(`/admin/crash_reports/terminals?f=${f}&page=${page}`, {
			cancelToken: options?.cancelToken,
		});
	}

	static async getCrashReportsOrganizationNamesSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	) {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);

		return restApi.get(`/admin/crash_reports/organization_names?f=${f}&page=${page}`, {
			cancelToken: options?.cancelToken,
		});
	}

	static async getCrashReportsAppNamesSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	) {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);

		return restApi.get(`/admin/crash_reports/app_names?f=${f}&page=${page}`, {
			cancelToken: options?.cancelToken,
		});
	}

	static async getCrashReportsAppVersionsSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	) {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);

		return restApi.get(`/admin/crash_reports/app_versions?f=${f}&page=${page}`, {
			cancelToken: options?.cancelToken,
		});
	}

	static restoreCrashReports(
		ids: number[],
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<CrashReportApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/admin/crash_reports/restore`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?id=${ids}&${urlParams}`;

		return restApi.put(endpoint);
	}

	static async restoreCrashReport(id: number, params?: Record<string, any>): Promise<CrashReportApi> {
		return parseApiData(restApi.put(`/admin/crash_reports/${id}/restore`, undefined, { params }));
	}

	static closeCrashReports(
		ids: number[],
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<CrashReportApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/admin/crash_reports/close`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?id=${ids}&${urlParams}`;

		return restApi.put(endpoint);
	}

	static async closeCrashReport(id: number, params?: Record<string, any>): Promise<CrashReportApi> {
		return parseApiData(restApi.put(`/admin/crash_reports/${id}/close`, undefined, { params }));
	}

	static startFixingCrashReports(
		ids: number[],
		params?: Record<string, any>,
		areAllItemsSelected?: boolean
	): Promise<CrashReportApi[]> {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/admin/crash_reports/start_fix`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?id=${ids}&${urlParams}`;

		return restApi.put(endpoint);
	}

	static async markAsPermanentlyBrokenCrashReports(
		ids: number[],
		data: { comment: string },
		params: Record<string, any>
	) {
		return restApi.put(`/admin/crash_reports/accept?id=${[...ids]}`, data, { params });
	}

	static async startFixCrashReport(id: number, params?: Record<string, any>): Promise<CrashReportApi> {
		return parseApiData(restApi.put(`/admin/crash_reports/${id}/start_fix`, undefined, { params }));
	}

	static async markAsPermanentlyBrokenCrashReport(
		id: number,
		data: { comment: string },
		params?: Record<string, any>
	): Promise<CrashReportApi> {
		return parseApiData(restApi.put(`/admin/crash_reports/${id}/accept`, data, { params }));
	}

	static async getCrashReportOccurrence(id: number, params?: Record<string, any>): Promise<CrashReportOccurrenceApi> {
		return parseApiData(restApi.get(`/admin/crash_reports/occurrences/${id}`, { params }));
	}

	static async getCrashReport(id: number, params?: Record<string, any>): Promise<CrashReportApi> {
		return parseApiData(restApi.get(`/admin/crash_reports/${id}`, { params }));
	}

	static async getCrashReports(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<CrashReportApi[]> {
		return parseApiData(restApi.get("/admin/crash_reports", { params, cancelToken: options?.cancelToken }));
	}

	static async getCrashReportsCount(params?: Record<string, any>, options?: Record<string, any>): Promise<number> {
		return parseApiData(
			restApi.get(`/admin/crash_reports/count`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getAdminUsers(params?: Record<string, any>, options?: Record<string, any>): Promise<UserApi[]> {
		return parseApiData(restApi.get("/admin/users", { params, cancelToken: options?.cancelToken }));
	}

	static async updateOrganizationAccesses(
		organizationId: number,
		params: Record<string, any>
	): Promise<OrganizationApi> {
		return parseApiData(
			restApi.put(`/admin/organizations/${organizationId}/update_accesses`, undefined, { params })
		);
	}

	// TODO co zwróci?
	static async changeOrganizationAccesses(
		organizationId: number,
		data: OrganizationAccessApi,
		params?: Record<string, any>
	): Promise<OrganizationApi> {
		return parseApiData(restApi.put(`/admin/organizations/${organizationId}/accesses`, data, { params }));
	}

	static async updateOrganizationApplication(
		data: OrganizationApplicationApi,
		organizationId: number,
		id: number
	): Promise<OrganizationApplicationApi> {
		const params = { include: "applications,applications.accesses,applications.properties" };
		return parseApiData(restApi.put(`/admin/organizations/${organizationId}/applications/${id}`, data, { params }));
	}

	static async getProductCatalog(): Promise<ProductCatalogApi> {
		return parseApiData(restApi.get(`/admin/tools/product_catalog`));
	}

	static async fixPartnerSubscription(): Promise<AxiosResponse> {
		return restApi.post(`/admin/tools/subscriptions/partner_fix`);
	}

	static async fixCompanyName(): Promise<AxiosResponse> {
		return restApi.post("/admin/tools/subscriptions/company_name_fix");
	}

	static async getWrongInvoices(): Promise<WrongInvoiceApi[]> {
		return parseApiData(restApi.get("/admin/tools/subscriptions/wrong_invoices"));
	}

	static async getAnomaliesPartnerSettlements(): Promise<AnomaliesPartnerSettlementApi[]> {
		return parseApiData(restApi.get("/admin/tools/subscriptions/anomalies_partner_settlements"));
	}

	static async fetchChargebee(): Promise<AxiosResponse> {
		return restApi.post("/admin/tools/download_chargebee");
	}

	static async updateOrganizationCf(): Promise<AxiosResponse> {
		return restApi.post("/admin/tools/update_organization_cf");
	}

	static async rebuildPriceListItem(): Promise<AxiosResponse> {
		return restApi.post("/admin/tools/rebuild_price_list_item");
	}

	static async setCustomersWithFixedPrice(): Promise<AxiosResponse> {
		return restApi.post("/admin/tools/set_customers_with_fixed_price");
	}

	static async updatePricesForExistingCustomers(): Promise<AxiosResponse> {
		return restApi.post("/admin/tools/update_prices_for_existing_customers");
	}

	static async fixCompaniesAndCustomers(): Promise<AxiosResponse> {
		return restApi.post("/admin/tools/fix_companies_and_customers");
	}

	static async rebuildProductCatalog(): Promise<ProductCatalogApi> {
		return parseApiData(restApi.put(`/admin/tools/product_catalog/rebuild`));
	}

	static async getSubscription(id: number): Promise<SubscriptionsApi> {
		return parseApiData(restApi.get(`/admin/organizations/${id}/subscription`));
	}

	static async autoSubscription(id: number) {
		return restApi.post(`/admin/organizations/${id}/subscription/auto`, {});
	}

	static async manualSubscription(id: number) {
		return restApi.post(`/admin/organizations/${id}/subscription/manual`, {});
	}

	static async blockSubscription(id: number) {
		return restApi.put(`/admin/organizations/${id}/block`);
	}

	static async unblockSubscription(id: number) {
		return restApi.put(`/admin/organizations/${id}/unblock`);
	}

	static async downloadFromChargebee(id: number) {
		return restApi.post(`/admin/organizations/${id}/sync_download_chargebee`);
	}

	static async downloadFeaturesUsage(id: number) {
		return restApi.post(`/admin/organizations/${id}/download_features_usage`);
	}

	static async updateCF(id: number) {
		return restApi.post(`/admin/organizations/${id}/update_cf`);
	}

	static async uploadToChargebee(id: number) {
		return restApi.post(`/admin/organizations/${id}/sync_upload_chargebee`);
	}

	static async changeSubscriptionTrial(id: number, data: { trial: boolean }) {
		return restApi.post(`/admin/organizations/${id}/subscription/trial`, data);
	}

	static async updateAdminOrganization(
		organizationId: number,
		data: OrganizationFormProps,
		params?: Record<string, any>
	): Promise<OrganizationApi> {
		return parseApiData(restApi.put(`/admin/organizations/${organizationId}`, data, { params }));
	}

	static async getAdminOrganization(organizationId: number, params?: Record<string, any>): Promise<OrganizationApi> {
		return parseApiData(restApi.get(`/admin/organizations/${organizationId}`, { params }));
	}

	static async getAdminOrganizations(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AdminOrganizationApi[]> {
		return parseApiData(restApi.get("/admin/organizations", { params, cancelToken: options?.cancelToken }));
	}

	static async getAdminOrganizationUsers(
		organizationId: number,
		params?: Record<string, any>
	): Promise<AdminOrganizationUserApi[]> {
		return parseApiData(restApi.get(`/admin/organizations/${organizationId}/users`, { params }));
	}

	static async resendInvitation(organizationId: number, invitationId: number) {
		return restApi.post(`/admin/organizations/${organizationId}/users/${invitationId}/resend`);
	}

	static async saveSegment(segment: SegmentApi) {
		return restApi.post(`/segments`, segment);
	}

	static async updateSegment(segment: SegmentApi) {
		return restApi.put(`/segments/${segment.id}`, segment);
	}

	static async sendEmailToPartners() {
		return restApi.post(`/admin/partners/summary/pdf_via_mail`);
	}

	static async closeLastMonthPayoffs() {
		return restApi.put(`/admin/partners/summary/close`);
	}

	static async summarizePayoff(id: number) {
		return restApi.put(`/partners/${id}/summarize`);
	}

	static async payPayoff(id: number) {
		return restApi.put(`/admin/partners/summary/${id}/pay`);
	}

	static async cancelPayPayoff(id: number) {
		return restApi.put(`/admin/partners/summary/${id}/cancel_pay`);
	}

	static async cancelPayPayoffs(ids: number[], params?: Record<string, any>, areAllItemsSelected?: boolean) {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/admin/partners/summary/cancel_pay`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?id=${ids}&${urlParams}`;

		return restApi.put(endpoint);
	}

	static async payPayoffs(ids: number[]) {
		return restApi.put(`/admin/partners/summary/pay?id=${[...ids]}`);
	}

	static async closePayoff(id: number) {
		return restApi.put(`/admin/partners/summary/${id}/close`);
	}

	static async closePayoffs(ids: number[], params?: Record<string, any>, areAllItemsSelected?: boolean) {
		const urlParams = new URLSearchParams(params).toString();
		const baseEndpoint = `/admin/partners/summary/close`;
		const endpoint = areAllItemsSelected
			? `${baseEndpoint}?${urlParams}`
			: `${baseEndpoint}?id=${ids}&${urlParams}`;

		return restApi.put(endpoint);
	}

	static async getTagsListSearchSelect(query: string): Promise<SearchSelectApi[]> {
		return parseApiData(restApi.get(`/tags/search_select?f=${query}`));
	}

	static async getPartnersInvoices(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<PayoutInvoiceApi[]> {
		return parseApiData(restApi.get("/admin/partners/invoices", { params, cancelToken: options?.cancelToken }));
	}

	static async confirmPartnerInvoice(id: string): Promise<AxiosResponse> {
		return restApi.put(`/admin/partners/invoices/${id}/confirm`);
	}

	static async rejectPartnerInvoice(id: string): Promise<AxiosResponse> {
		return restApi.put(`/admin/partners/invoices/${id}/deny`);
	}

	static async cancelPaymentForPartnerInvoice(id: string): Promise<AxiosResponse> {
		return restApi.put(`/admin/partners/invoices/${id}/cancel_payment`);
	}

	static async cancelConfirmForPartnerInvoice(id: string): Promise<AxiosResponse> {
		return restApi.put(`/admin/partners/invoices/${id}/cancel_confirm`);
	}

	static async payForPartnerInvoice(id: string): Promise<AxiosResponse> {
		return restApi.put(`/admin/partners/invoices/${id}/pay`);
	}

	static async updatePartnerInvoice(id: string, data: PayoutInvoiceFormProps): Promise<AxiosResponse> {
		return restApi.put(`/admin/partners/invoices/${id}`, data);
	}

	static async createPayoffTransaction(summaryId: number, data: PayoffTransactionFormProps): Promise<AxiosResponse> {
		return restApi.post(`/admin/partners/summary/${summaryId}/transactions`, data);
	}

	static async createPayoff(partnerId: number): Promise<AxiosResponse> {
		return restApi.post(`/admin/partners/summary`, { partner_id: partnerId });
	}

	static async deletePayoffTransaction(summaryId: number, transactionId: number): Promise<AxiosResponse> {
		return restApi.delete(`/admin/partners/summary/${summaryId}/transactions/${transactionId}`);
	}

	static async getLoans(params?: Record<string, any>, options?: Record<string, any>): Promise<LoanApi[]> {
		return parseApiData(restApi.get(`/admin/loans`, { params, cancelToken: options?.cancelToken }));
	}

	static async createSubscriptionFromLoan(id: number) {
		return restApi.put(`/admin/loans/${id}/subscription`);
	}

	static async getLoan(id: number, params?: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.get(`/admin/loans/${id}`, { params }));
	}

	static async openLoan(id: number, params: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.put(`/admin/loans/${id}/open`, undefined, { params }));
	}

	static async closeLoan(id: number, params: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.put(`/admin/loans/${id}/close`, undefined, { params }));
	}

	static async cancelAcceptLoan(id: number, params: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.put(`/admin/loans/${id}/cancel_sign`, undefined, { params }));
	}

	static async getLoanPdf(id: number) {
		return restApi.get(`/admin/loans/${id}/pdf`, {
			responseType: "blob",
		});
	}

	static async sendLoanViaEmail(id: number, data: { email: string }) {
		return restApi.post(`/admin/loans/${id}/pdf_via_mail`, data);
	}

	static async sendLoanEmailToClient(id: number, data: { email: string }) {
		return restApi.post(`/admin/loans/${id}/send_mail`, data);
	}

	static async updateLoan(data: any, params?: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.put(`/admin/loans/${data.id}`, data, { params }));
	}

	static async createLoan(data: LoanApi): Promise<LoanApi> {
		return parseApiData(restApi.post(`/admin/loans`, data));
	}

	static async cancelLoan(id: number, data: CancelLoanFormProps, params?: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.delete(`/admin/loans/${id}`, { data, params }));
	}

	static async approveLoanCancellation(id: number, params?: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.put(`/admin/loans/${id}/approve_cancellation`, undefined, { params }));
	}

	static async rejectLoanCancellation(id: number, params?: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.put(`/admin/loans/${id}/reject_cancellation`, undefined, { params }));
	}

	static async acceptLoan(id: number, params?: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.put(`/admin/loans/${id}/sign`, undefined, { params }));
	}

	static async getCustomFields(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<CustomFieldTemplateApi[]> {
		return parseApiData(restApi.get("/custom_fields", { params, cancelToken: options?.cancelToken }));
	}

	static async updateCustomField(
		client: CustomFieldTemplateFormApi,
		params?: Record<string, any>
	): Promise<CustomFieldTemplateApi> {
		return parseApiData(restApi.put(`/custom_fields/${client.id}`, client, { params }));
	}

	static async createCustomField(client: CustomFieldTemplateFormApi): Promise<CustomFieldTemplateApi> {
		return parseApiData(restApi.post(`/custom_fields`, client));
	}

	static async getCustomField(id: number, params?: Record<string, any>): Promise<CustomFieldTemplateApi> {
		return parseApiData(restApi.get(`/custom_fields/${id}`, { params }));
	}

	static async removeCustomField(id: number): Promise<CustomFieldTemplateApi> {
		return parseApiData(restApi.delete(`/custom_fields/${id}`));
	}

	static async restoreCustomField(id: number): Promise<CustomFieldTemplateApi> {
		return parseApiData(restApi.put(`/custom_fields/${id}/restore`));
	}

	static async getSupportAccessOrganizations(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<SupportAccessOrganizationApi[]> {
		return parseApiData(
			restApi.get("/admin/support_access_organizations", { params, cancelToken: options?.cancelToken })
		);
	}

	static async searchSupportAccessOrganizations(params?: Record<string, any>): Promise<{
		organizations: SupportOrganizationApi[];
		users: UserApi[];
	}> {
		return parseApiData(
			restApi.get(`/admin/support_access_organizations/search`, {
				params,
			})
		);
	}

	static async mergeOrganization(organizationId: number, data: MergeOrganizationApi): Promise<AxiosResponse> {
		return restApi.post(`/admin/organizations/${organizationId}/merge`, data);
	}

	static async removeOrganization(data: RemoveOrganizationApi, organizationId: number): Promise<AxiosResponse> {
		const config = { data } as AxiosRequestConfig;
		return restApi.delete(`/admin/organizations/${organizationId}`, config);
	}

	static async getOrganizationsSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);
		return restApi.get(`organizations/search_select?f=${f}=page=${page}`, {
			cancelToken: options?.cancelToken,
		});
	}

	static async getIntercomConversations(
		organizationId: number,
		params?: Record<string, any>
	): Promise<IntercomConversationApi[]> {
		return parseApiData(restApi.get(`/admin/organizations/${organizationId}/intercom/conversations`, { params }));
	}

	static async getOrganizationPartners(
		organizationId: number,
		params?: Record<string, any>
	): Promise<OrganizationPartnerInfoApi[]> {
		return parseApiData(restApi.get(`/admin/organizations/${organizationId}/partners`, { params }));
	}

	static async getListAppVersionsSearchSelect(search?: string): Promise<AppVersionSearchSelectApi[]> {
		const query = btoa(unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}query|c=${search}`)));
		return parseApiData(restApi.get(`/admin/app_versions/search_select?f=${query}`));
	}

	static async getTransactionsReport(params?: Record<string, any>, options?: Record<string, any>) {
		return restApi.get(`/admin/transactions/report`, { params, cancelToken: options?.cancelToken });
	}

	static async getOrganizationFeatureReport(params?: Record<string, any>, options?: Record<string, any>) {
		return restApi.get(`/admin/organization_feature/report`, { params, cancelToken: options?.cancelToken });
	}

	static async getOrganizationFeatureUsageReport(params?: Record<string, any>, options?: Record<string, any>) {
		return restApi.get(`/admin/organization_feature/usage_report`, { params, cancelToken: options?.cancelToken });
	}

	static async getTransactionsReportSyncInfo(params?: Record<string, any>): Promise<AxiosResponse> {
		return restApi.get(`/admin/transactions/sync_info`, { params });
	}

	static async syncTransactionsReport(data: SyncTransactionReportApi): Promise<AxiosResponse> {
		return restApi.post(`/admin/transactions/sync`, data);
	}

	static async syncOrganizationFeatures(data: SyncOrganizationFeatureReportApi): Promise<AxiosResponse> {
		return restApi.post(`/admin/organization_feature/sync`, data);
	}

	static async getTransactions(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<TransactionApi[]> {
		return parseApiData(restApi.get(`/admin/transactions`, { params, cancelToken: options?.cancelToken }));
	}

	static async getAccess(params?: Record<string, any>, options?: Record<string, any>): Promise<AccessApi[]> {
		return parseApiData(
			restApi.get(`/admin/organization_application_accesses`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getAccessTypes(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);
		return restApi.get(`/admin/organization_application_accesses/types/search_select?f=${f}&page=${page}`, {
			cancelToken: options?.cancelToken,
		});
	}

	static async getAdminAccessReport(params?: Record<string, any>, options?: Record<string, any>) {
		return restApi.get(`/admin/organization_application_accesses/report`, {
			params,
			cancelToken: options?.cancelToken,
		});
	}

	static async getTransaction(transactionId: number, params?: Record<string, any>): Promise<TransactionApi> {
		return parseApiData(restApi.get(`/admin/transactions/${transactionId}`, { params }));
	}

	static async createTransaction(data: TransactionApi, params?: Record<string, any>): Promise<TransactionApi> {
		return parseApiData(restApi.post(`/admin/transactions`, data, { params }));
	}

	static async updateTransaction(
		transactionId: number,
		data: TransactionApi,
		params?: Record<string, any>
	): Promise<TransactionApi> {
		return parseApiData(restApi.put(`/admin/transactions/${transactionId}`, data, { params }));
	}

	static async removeTransaction(transactionId: number): Promise<AxiosResponse> {
		return restApi.delete(`/admin/transactions/${transactionId}`);
	}

	static async getListTransactionsSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);
		return restApi.get(`/admin/transactions/search_select?f=${f}&page=${page}`, {
			cancelToken: options?.cancelToken,
		});
	}

	static async getCompaniesReport(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		return restApi.get(`/admin/companies/report`, { params, cancelToken: options?.cancelToken });
	}

	static async syncCompanies(data: SyncCompaniesFormProps): Promise<AxiosResponse> {
		return restApi.post("/admin/tools/sync_companies", data);
	}

	static async getOrganizationsDashboard(): Promise<OrganizationsDashboardApi> {
		return parseApiData(restApi.get("/admin/organizations/dashboard"));
	}

	static async clearCache(): Promise<AxiosResponse> {
		return restApi.post("/admin/tools/partner_transaction_cache/clear");
	}

	static async generateLoanTerminationFile(loanId: number): Promise<AxiosResponse> {
		return restApi.get(`/admin/loans/${loanId}/terminate/pdf`, {
			responseType: "blob",
		});
	}

	static async getTerminals(
		organizationId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<TerminalApi[]> {
		return parseApiData(
			restApi.get(`/admin/organizations/${organizationId}/terminals`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getInvoices(
		organizationId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<SubscriptionInvoiceApi[]> {
		return parseApiData(
			restApi.get(`/admin/organizations/${organizationId}/invoices`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getSubscriptionProformaPdf(organizationId: number, proformaId: string): Promise<AxiosResponse> {
		return restApi.get(`/admin/organizations/${organizationId}/invoices/proforma/${proformaId}/pdf`, {
			responseType: "blob",
		});
	}

	static async extendSubscriptionPayment(id: number) {
		return restApi.post(`/admin/organizations/${id}/extend_payment`);
	}

	static async getAppProviderTypes(options?: Record<string, any>): Promise<string[]> {
		return parseApiData(
			restApi.get(`/admin/app_templates/providers`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async changeOrganizationType(organizationId: number, type: OrganizationTypeApi): Promise<AxiosResponse> {
		return restApi.post(`/admin/organizations/${organizationId}/type`, { type });
	}

	static async getBillingCustomer(
		organizationId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<BillingCustomerApi> {
		return parseApiData(
			restApi.get(`/admin/organizations/${organizationId}/customer`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async updateBillingDate(organizationId: number, data: UpdateBillingDateApi): Promise<BillingCustomerApi> {
		return parseApiData(restApi.post(`/admin/organizations/${organizationId}/update_billing_date`, data));
	}

	static async getOrganizationProducts(
		organizationId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<OrganizationProductApi[]> {
		return parseApiData(
			restApi.get(`admin/organizations/${organizationId}/products`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getOrganizationFeatures(
		organizationId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<OrganizationFeatureApi[]> {
		return parseApiData(
			restApi.get(`admin/organizations/${organizationId}/features`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async updateOrganizationFeatures(
		organizationId: number,
		data: OrganizationProductFormApi,
		params?: Record<string, any>
	): Promise<OrganizationFeatureApi[]> {
		return parseApiData(
			restApi.put(`admin/organizations/${organizationId}/features`, data, {
				params,
			})
		);
	}

	static async getOrganizationFeaturesInfo(organizationId: number, options?: Record<string, any>): Promise<any> {
		return parseApiData(
			restApi.get(`admin/organizations/${organizationId}/features/info`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getOrganizationProductsPerCategory(
		organizationId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<OrganizationProductPerCategoryApi[]> {
		return parseApiData(
			restApi.get(`admin/organizations/${organizationId}/products_per_category`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async updateOrganizationProducts(
		organizationId: number,
		data: OrganizationProductFormApi,
		params?: Record<string, any>
	): Promise<OrganizationProductApi[]> {
		return parseApiData(
			restApi.put(`admin/organizations/${organizationId}/products`, data, {
				params,
			})
		);
	}

	static async rewriteOrganizationProducts(
		organizationId: number,
		params?: Record<string, any>
	): Promise<OrganizationApi> {
		return parseApiData(
			restApi.put(`admin/organizations/${organizationId}/update_products`, undefined, {
				params,
			})
		);
	}

	// GO LAUNCHER CATEGORY

	static async getGoLauncherCategories(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<GoLauncherCategoryApi[]> {
		return parseApiData(
			restApi.get("golauncher/categories", {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async removeGoLauncherCategory(categoryId: number): Promise<{}> {
		return parseApiData(restApi.delete(`golauncher/categories/${categoryId}`));
	}

	static async createGoLauncherCategory(data: GoLauncherCategoryApi): Promise<GoLauncherCategoryApi> {
		return parseApiData(restApi.post("golauncher/categories", data));
	}

	static async updateGoLauncherCategory(
		categoryId: number,
		data: GoLauncherCategoryApi
	): Promise<GoLauncherCategoryApi> {
		return parseApiData(restApi.put(`golauncher/categories/${categoryId}`, data));
	}

	static async getGoLauncherCategory(categoryId: number): Promise<GoLauncherCategoryApi> {
		return parseApiData(restApi.get(`golauncher/categories/${categoryId}`));
	}

	static async getGoLauncherApplicationsSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<SearchSelectApi[]> {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);
		return parseApiData(
			restApi.get(`golauncher/categories/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	// GO LAUNCHER APPLICATION

	static async getGoLauncherApplications(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<GoLauncherApplicationApi[]> {
		return parseApiData(
			restApi.get("golauncher/applications", {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getGoLauncherApplicationTypes(search: string, options?: Record<string, any>): Promise<string[]> {
		const f = mapToBase(search, {});
		return parseApiData(
			restApi.get(`golauncher/applications/types?f=${f}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getGoLauncherApplicationCustomFeatures(
		search: string,
		options?: Record<string, any>
	): Promise<string[]> {
		const f = mapToBase(search, {});
		return parseApiData(
			restApi.get(`golauncher/applications/custom_features?f=${f}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getGoLauncherApplicationSupportedVersions(
		search: string,
		options?: Record<string, any>
	): Promise<string[]> {
		const f = mapToBase(search, {});
		return parseApiData(
			restApi.get(`golauncher/applications/version_statuses?f=${f}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async createGoLauncherApplication(data: GoLauncherApplicationApi): Promise<GoLauncherApplicationApi> {
		return parseApiData(restApi.post("golauncher/applications", data));
	}

	static async updateGoLauncherApplication(
		applicationId: number,
		data: GoLauncherApplicationApi
	): Promise<GoLauncherApplicationApi> {
		return parseApiData(restApi.put(`golauncher/applications/${applicationId}`, data));
	}

	static async getGoLauncherApplication(applicationId: number): Promise<GoLauncherApplicationApi> {
		return parseApiData(restApi.get(`golauncher/applications/${applicationId}`));
	}

	static async removeGoLauncherApplication(applicationId: number): Promise<{}> {
		return parseApiData(restApi.delete(`golauncher/applications/${applicationId}`));
	}

	static async searchSupportDistricts(data: SearchSupportDistrictApi): Promise<SupportDistrictApi[]> {
		return parseApiData(restApi.put("admin/support_actions/districts", data));
	}

	static async getCurrentDistricts(data: SearchSupportDistrictApi): Promise<SupportDistrictApi[]> {
		return parseApiData(
			restApi.get("admin/support_actions/streets", {
				params: {
					street: data.street,
					city: data.city,
				},
			})
		);
	}

	static async getDistricts(city: string): Promise<string[]> {
		return parseApiData(
			restApi.get("admin/support_actions/districts", {
				params: {
					city,
				},
			})
		);
	}
}

export { adminApi };
